/* Created: Browser-side entry points for transactional mail (all sending happens on the server via Brevo).
   - invokeBrevoMail: calls the brevo-mail function (preview, test mail, webhook status) with readable errors.
   - setOrderStatus / resendOrderMail / sendDocumentMail: call the admin database actions that validate
     rights and data on the server and queue at most one mail per action id.
   - describeQueueResult: Dutch toast text for the result of such an action.
   Replaces the former EmailJS-based order-email-service.
   Updated: previewDocument returns the exact mail + PDF the server will send for a stored document, with a
   version fingerprint; sendDocumentMail must pass that fingerprint so a changed document is never sent unseen.
   updateDocumentDraft saves edits to an unsent draft under the same number.
   Updated: MAIL_TYPE_LABELS shared by admin pages; previewStatusMail asks the server which customer mail a
   status change would send (or why none); the "no mail" result says plainly that the customer got nothing. */

import { supabase } from '@/lib/supabase';

export const TEST_MAIL_RECIPIENT = 'julan.tw@gmail.com';

export const MAIL_TYPE_LABELS: Record<string, string> = {
  order_pending_bank_transfer: 'Bevestiging bankoverschrijving (met factuur)',
  order_paid_confirmation: 'Betaalbevestiging online betaling via Mollie (met factuur)',
  order_status_confirmed: 'Status: bestelling bevestigd',
  order_status_processing: 'Status: in behandeling',
  order_status_shipped: 'Status: verzonden',
  order_status_delivered: 'Status: geleverd (met reviewverzoek)',
  order_status_cancelled: 'Status: geannuleerd',
  document_invoice: 'Factuur vanuit beheer (met factuur-PDF)',
  document_quote: 'Offerte vanuit beheer (met offerte-PDF)',
};

export async function invokeBrevoMail<T = Record<string, unknown>>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('brevo-mail', { body });
  if (error) {
    let message = 'Onbekende fout';
    try {
      const ctx = (error as { context?: Response }).context;
      const parsed = ctx ? await ctx.json() : null;
      if (parsed?.error) message = parsed.error;
    } catch { /* keep generic message */ }
    throw new Error(message);
  }
  if (!data || typeof data !== 'object') throw new Error('Leeg antwoord van de mailserver');
  return data as T;
}

export interface QueueResult {
  changed?: boolean;
  mail: 'queued' | 'duplicate' | 'none' | 'skipped';
  reason?: string;
  job?: { job_id: string; created: boolean; status: string; mail_type: string } | null;
}

function parseResult(data: unknown): QueueResult {
  if (!data || typeof data !== 'object' || typeof (data as QueueResult).mail !== 'string') {
    throw new Error('Onverwacht antwoord van de server');
  }
  return data as QueueResult;
}

export async function setOrderStatus(orderId: string, status: string, sendMail: boolean, actionId: string) {
  const { data, error } = await supabase.rpc('admin_set_order_status', {
    p_order_id: orderId, p_status: status, p_send_mail: sendMail, p_action_id: actionId,
  });
  if (error) throw new Error(error.message);
  return parseResult(data);
}

export async function resendOrderMail(orderId: string, actionId: string) {
  const { data, error } = await supabase.rpc('admin_resend_order_mail', { p_order_id: orderId, p_action_id: actionId });
  if (error) throw new Error(error.message);
  return parseResult(data);
}

export interface StatusMailPlan {
  mailType: string | null;
  skipReason: string | null;
}

export async function previewStatusMail(orderId: string, status: string): Promise<StatusMailPlan> {
  const { data, error } = await supabase.rpc('admin_preview_status_mail', { p_order_id: orderId, p_status: status });
  if (error) throw new Error(error.message);
  if (!data || typeof data !== 'object') throw new Error('Onverwacht antwoord van de server');
  const plan = data as { mail_type?: unknown; skip_reason?: unknown };
  return {
    mailType: typeof plan.mail_type === 'string' ? plan.mail_type : null,
    skipReason: typeof plan.skip_reason === 'string' ? plan.skip_reason : null,
  };
}

export async function sendDocumentMail(documentId: string, actionId: string, fingerprint: string) {
  const { data, error } = await supabase.rpc('admin_send_document', {
    p_document_id: documentId, p_action_id: actionId, p_fingerprint: fingerprint,
  });
  if (error) throw new Error(error.message);
  return parseResult(data);
}

export interface DocumentPreview {
  fingerprint: string;
  paid: boolean;
  subject: string;
  to: string[];
  bcc: string[];
  html: string;
  attachment: { name: string; pdf_base64: string };
}

export async function previewDocument(documentId: string): Promise<DocumentPreview> {
  const data = await invokeBrevoMail<DocumentPreview>({ action: 'document_preview', document_id: documentId });
  if (typeof data.fingerprint !== 'string' || !data.attachment?.pdf_base64) throw new Error('Onvolledig voorbeeld ontvangen');
  return data;
}

export async function updateDocumentDraft(documentId: string, payload: Record<string, unknown>): Promise<string> {
  const { data, error } = await supabase.rpc('admin_update_document_draft', { p_document_id: documentId, p_doc: payload });
  if (error) throw new Error(error.message);
  if (typeof data !== 'string') throw new Error('Onverwacht antwoord van de server');
  return data;
}

export function pdfBase64ToBlob(base64: string): Blob {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return new Blob([bytes], { type: 'application/pdf' });
}

export function describeQueueResult(result: QueueResult, recipient: string): { kind: 'success' | 'info' | 'warning'; title: string; description?: string } {
  switch (result.mail) {
    case 'queued':
      return { kind: 'success', title: `Mail naar ${recipient} staat klaar voor verzending via Brevo`, description: 'Met kopie naar info@buitnplanten.nl. De bezorgstatus zie je bij Transactiemails.' };
    case 'duplicate':
      return { kind: 'info', title: 'Deze mail was al ingepland', description: 'Er is geen tweede mail aangemaakt.' };
    case 'skipped':
      return { kind: 'warning', title: 'Geen mail verstuurd', description: result.reason };
    default:
      if (result.changed === false) return { kind: 'info', title: 'Status was al ingesteld; er is niets gewijzigd' };
      return {
        kind: 'warning',
        title: 'Status gewijzigd – er is géén mail naar de klant verstuurd',
        description: 'Wil je de klant toch mailen? Gebruik de mailknop (envelop) bij deze bestelling.',
      };
  }
}
